import { computed, ref } from 'vue';
import { appThemes, createAppearancePalette, createArtworkRamp, defaultAppearance, isDarkTheme,
  normalizeAppearance, normalizeThemeHue, resolveAppearanceColors, mainBrightnessRange,
  type AppearanceSettings, type AppTheme, type BackgroundArtwork, type ColorAdjustment } from '@pror0/appearance';
import { colorizeArtwork, artworkSources, artworkOptions } from '@pror0/appearance/artwork';
export type { AppTheme, BackgroundArtwork } from '@pror0/appearance';

export { themeOptions } from '@pror0/appearance';
export const backgroundArtworkOptions = artworkOptions;
const accountKey = (userId?: string | null) => userId || 'anonymous';
export const appearanceStorageKey = (theme: AppTheme, userId?: string | null) => `geobu:appearance:v1:${theme}:${accountKey(userId)}`;
const themeStorageKey = (userId?: string | null) => `geobu:theme:${accountKey(userId)}`;
const sessionSettings = new Map<string, Record<AppTheme, AppearanceSettings>>();
const sessionThemes = new Map<string, AppTheme>();
const unsavedThemes = new Set<string>();
const unsavedSettings = new Map<string, AppearanceSettings>();
let account = 'anonymous';
let appliedProperties: string[] = [];
let frame: number | undefined;
export const activeTheme = ref<AppTheme>('light');
const settings = ref<Record<AppTheme, AppearanceSettings>>(Object.fromEntries(appThemes.map(theme => [theme, defaultAppearance(theme)])) as Record<AppTheme, AppearanceSettings>);
export const activeAppearance = computed(() => settings.value[activeTheme.value]);
export const activeThemeHue = computed(() => activeAppearance.value.hueOffset);
export const activeBackgroundArtwork = computed(() => activeAppearance.value.artwork);
export const activeAppearanceColors = computed(() => resolveAppearanceColors(activeTheme.value, activeAppearance.value));
export const activeArtworkRamp = computed(() => createArtworkRamp(activeTheme.value, activeAppearance.value));

// Keep persisted values within the usable range when another color setting changes.
function normalizeSavedAppearance(theme: AppTheme, value: unknown): AppearanceSettings {
  const normalized = normalizeAppearance(theme, value);
  const { min, max } = mainBrightnessRange(theme, normalized);
  return { ...normalized, mainBrightness: Math.max(min, Math.min(max, normalized.mainBrightness)) };
}

function persist(theme: AppTheme, value: AppearanceSettings) {
  const key = appearanceStorageKey(theme, account);
  try { localStorage.setItem(key, JSON.stringify(value)); unsavedSettings.delete(key); }
  catch { unsavedSettings.set(key, value); }
}

function loadAccount(userId?: string | null) {
  account = accountKey(userId);
  const next = {} as Record<AppTheme, AppearanceSettings>;
  for (const theme of appThemes) {
    const unsaved = unsavedSettings.get(appearanceStorageKey(theme, account));
    if (unsaved) { next[theme] = unsaved; continue; }
    try {
      const stored = localStorage.getItem(appearanceStorageKey(theme, account));
      if (stored !== null) {
        try { next[theme] = normalizeSavedAppearance(theme, JSON.parse(stored)); }
        catch { next[theme] = defaultAppearance(theme); }
      } else {
        const legacyWash = localStorage.getItem(`geobu:artwork-wash:${isDarkTheme(theme) ? 'dark' : 'light'}:${account}`);
        next[theme] = normalizeSavedAppearance(theme, { ...defaultAppearance(theme),
          wash: legacyWash !== null && /^\d+$/.test(legacyWash) ? Number(legacyWash) : defaultAppearance(theme).wash,
        });
        persist(theme, next[theme]);
      }
    } catch { next[theme] = sessionSettings.get(account)?.[theme] ?? defaultAppearance(theme); }
  }
  settings.value = next;
  sessionSettings.set(account, next);
}

function applyToDocument() {
  frame = undefined;
  const theme = activeTheme.value, appearance = activeAppearance.value;
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = isDarkTheme(theme) ? 'dark' : 'light';
  root.classList?.toggle('dark', isDarkTheme(theme));
  const defaults = defaultAppearance(theme);
  root.dataset.customBackground = String(!appearance.background.automatic || appearance.minBrightness !== defaults.minBrightness
    || appearance.maxBrightness !== defaults.maxBrightness || appearance.wash !== defaults.wash);
  for (const property of appliedProperties) root.style.removeProperty(property);
  const palette = createAppearancePalette(theme, appearance);
  appliedProperties = Object.keys(palette);
  for (const [property, value] of Object.entries(palette)) root.style.setProperty(property, value);
  root.style.setProperty('--ui-artwork-wash-opacity', String(appearance.wash / 100));
  root.style.setProperty('--ui-artwork-wash-color', isDarkTheme(theme) ? '0 0 0' : '255 255 255');
  // With artwork disabled, the canvas is used directly, without a wash.
  root.style.setProperty('--ui-artwork-wash', appearance.showArtwork ? 'rgb(var(--ui-artwork-wash-color) / var(--ui-artwork-wash-opacity))' : 'transparent');
  root.style.setProperty('--ui-artwork-url', appearance.showArtwork
    ? colorizeArtwork(artworkSources[appearance.artwork], activeArtworkRamp.value.tables) : 'none');
}

function scheduleApply() {
  if (typeof requestAnimationFrame !== 'function') { applyToDocument(); return; }
  if (frame === undefined) frame = requestAnimationFrame(applyToDocument);
}
function applyImmediately() {
  if (frame !== undefined && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
  applyToDocument();
}

export function initializeTheme(userId?: string | null) {
  loadAccount(userId);
  try {
    const stored = localStorage.getItem(themeStorageKey(account));
    activeTheme.value = unsavedThemes.has(account) ? sessionThemes.get(account) ?? 'light'
      : appThemes.includes(stored as AppTheme) ? stored as AppTheme : 'light';
  } catch { activeTheme.value = sessionThemes.get(account) ?? 'light'; }
  sessionThemes.set(account, activeTheme.value);
  applyImmediately();
}

export function setTheme(theme: AppTheme, userId?: string | null) {
  if (!appThemes.includes(theme)) return;
  if (account !== accountKey(userId)) loadAccount(userId);
  activeTheme.value = theme;
  sessionThemes.set(account, theme);
  try { localStorage.setItem(themeStorageKey(account), theme); unsavedThemes.delete(account); }
  catch { unsavedThemes.add(account); }
  applyImmediately();
}

export function updateAppearance(patch: Partial<AppearanceSettings>, userId?: string | null) {
  if (account !== accountKey(userId)) loadAccount(userId);
  const theme = activeTheme.value, previous = activeAppearance.value;
  // A dragged thumb stops at the other thumb; equal endpoints intentionally flatten the artwork.
  if (patch.minBrightness !== undefined) patch = { ...patch, minBrightness: Math.min(previous.maxBrightness, patch.minBrightness) };
  if (patch.maxBrightness !== undefined) patch = { ...patch, maxBrightness: Math.max(previous.minBrightness, patch.maxBrightness) };
  const value = normalizeSavedAppearance(theme, { ...previous, ...patch });
  settings.value = { ...settings.value, [theme]: value };
  sessionSettings.set(account, settings.value);
  persist(theme, value);
  scheduleApply();
}
export function setThemeHue(value: number, userId?: string | null) { updateAppearance({ hueOffset: normalizeThemeHue(value) }, userId); }
export function setBackgroundArtwork(value: BackgroundArtwork, userId?: string | null) { updateAppearance({ artwork: value }, userId); }
export function setAutomaticColor(role: 'accent' | 'background', automatic: boolean, userId?: string | null) {
  if (account !== accountKey(userId)) loadAccount(userId);
  const current = activeAppearance.value[role], effective = activeAppearanceColors.value[role];
  const manual = current.manual ?? { hue: Math.round(effective.hue) % 360, strength: Math.round(effective.strength) };
  updateAppearance({ [role]: { automatic, manual } }, userId);
}
export function setManualColor(role: 'accent' | 'background', patch: Partial<ColorAdjustment>, userId?: string | null) {
  if (account !== accountKey(userId)) loadAccount(userId);
  const current = activeAppearance.value[role];
  updateAppearance({ [role]: { automatic: false, manual: { ...activeAppearanceColors.value[role], ...current.manual, ...patch } } }, userId);
}
export function resetThemeAppearance(userId?: string | null) {
  if (account !== accountKey(userId)) loadAccount(userId);
  const theme = activeTheme.value, value = defaultAppearance(theme);
  settings.value = { ...settings.value, [theme]: value };
  sessionSettings.set(account, settings.value);
  persist(theme, value);
  scheduleApply();
}

export function useTheme() {
  return { activeTheme, activeAppearance, initializeTheme, initTheme: initializeTheme, setTheme,
    isDark: () => isDarkTheme(activeTheme.value) };
}
